// Membership logic for the "Add to hike list" modal.
//
// GraphQL returns hike ids as strings, but the pages that seed the store set
// window.hike_id inconsistently (hikes/show renders a string, hikes/map a
// number), so every membership check must compare ids loosely. These are pure
// functions shared by the store action and the component so both sides apply
// the same rule.

export interface HikeListHike {
  id: string | number;
}

export interface HikeListLike {
  id: string | number;
  hikes?: HikeListHike[] | null;
  hikesCount?: number;
  /** Which server-kept list this is (HikeListKindEnum); null for a list the user made. */
  kind?: string | null;
}

/** HikeListKindEnum's value for the Completed list. */
export const COMPLETED_LIST_KIND = 'COMPLETED';

export function isCompletedList(hikeList: HikeListLike): boolean {
  return hikeList.kind === COMPLETED_LIST_KIND;
}

/**
 * Whether ticking this list should first ask when the hike was completed: adding to the
 * Completed list marks the hike completed, so it takes the same date as the hike page's
 * Completed button. Unticking it (and every other list) toggles straight away.
 */
export function asksForCompletedDate(hikeList: HikeListLike, hikeId: string | number | null | undefined): boolean {
  return isCompletedList(hikeList) && !isHikeInList(hikeList, hikeId);
}

export function sameHikeId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

export function isHikeInList(hikeList: HikeListLike, hikeId: string | number | null | undefined): boolean {
  return hikeList.hikes?.some((hike) => sameHikeId(hike.id, hikeId)) ?? false;
}

// Flips the hike's membership in one list and returns a new array, leaving the
// input (and every untouched list) as-is so the caller can re-flip to roll back
// a failed mutation. List order is preserved — the open modal must not shuffle.
export function toggleHikeMembership<T extends HikeListLike>(
  hikeLists: T[],
  hikeListId: string | number,
  hikeId: string | number,
): T[] {
  return hikeLists.map((list) => {
    if (!sameHikeId(list.id, hikeListId)) return list;
    const hikes = list.hikes ?? [];
    if (isHikeInList(list, hikeId)) {
      return {
        ...list,
        hikes: hikes.filter((hike) => !sameHikeId(hike.id, hikeId)),
        ...(list.hikesCount != null ? { hikesCount: Math.max(0, list.hikesCount - 1) } : {}),
      };
    }
    return {
      ...list,
      hikes: [...hikes, { id: String(hikeId) }],
      ...(list.hikesCount != null ? { hikesCount: list.hikesCount + 1 } : {}),
    };
  });
}
